import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { COUNTRIES_SORTED, COUNTRY_BY_CODE, flagEmoji } from "@/data/countries";

// Searchable country combobox with flags (all ISO countries).
export const CountryCombobox = ({ value, onChange, placeholder = "Selecciona tu país", testId = "country-combobox", className = "" }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = value ? COUNTRY_BY_CODE[String(value).toUpperCase()] : null;
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return COUNTRIES_SORTED;
    const norm = (t) => t.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    return COUNTRIES_SORTED.filter((c) => norm(c.name).includes(norm(s)) || c.code.toLowerCase() === s);
  }, [q]);

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          aria-expanded={open}
          className={`w-full flex items-center justify-between gap-2 rounded-md border border-white/10 bg-black/40 px-3 py-2.5 text-sm text-left ${selected ? "text-white" : "text-neutral-500"} hover:border-[#d4af37]/40 ${className}`}
        >
          <span className="flex items-center gap-2 truncate">
            {selected ? <><span className="text-lg leading-none">{selected.flag}</span><span className="truncate">{selected.name}</span></> : placeholder}
          </span>
          <ChevronsUpDown className="w-4 h-4 text-neutral-500 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[260px] bg-[#0a0a0a] border-[#d4af37]/30 text-white">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10">
          <Search className="w-4 h-4 text-neutral-500" />
          <input
            autoFocus
            data-testid={`${testId}-search`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar país…"
            className="bg-transparent outline-none text-sm w-full placeholder:text-neutral-600"
          />
        </div>
        <div className="max-h-64 overflow-y-auto py-1" data-testid={`${testId}-list`}>
          {list.length === 0 && <div className="px-3 py-3 text-xs text-neutral-500 italic">Sin resultados</div>}
          {list.map((c) => (
            <button
              key={c.code}
              type="button"
              data-testid={`${testId}-option-${c.code}`}
              onClick={() => { onChange?.(c.code); setOpen(false); setQ(""); }}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-white/10 ${value === c.code ? "text-[#d4af37]" : "text-neutral-200"}`}
            >
              <span className="text-lg leading-none">{flagEmoji(c.code)}</span>
              <span className="flex-1 truncate">{c.name}</span>
              <span className="text-[10px] text-neutral-600 font-mono">{c.code}</span>
              {value === c.code && <Check className="w-3.5 h-3.5" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default CountryCombobox;
